'use client'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { PlatformBadge } from '@/components/ui/platform-badge'
import { cn } from '@/lib/utils'
import { useWallet } from '@/providers/WalletContext'
import { useModalStore } from '@/stores/modal-store'
import { deriveSafeAddress } from '@/utils/safe'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

// Separate deposit IDs for each chain (Solana falls back to Polygon if not set)
const MOONPAY_POLYGON_DEPOSIT_ID = process.env.NEXT_PUBLIC_MOONPAY_POLYGON_DEPOSIT_ID || ''
const MOONPAY_SOLANA_DEPOSIT_ID =
	process.env.NEXT_PUBLIC_MOONPAY_SOLANA_DEPOSIT_ID || MOONPAY_POLYGON_DEPOSIT_ID

type DepositPlatform = 'polymarket' | 'kalshi'

export function MoonPayDepositModal() {
	const { activeModal, closeModal, openModal } = useModalStore()
	const isOpen = activeModal === 'moonpay-deposit'
	const { eoaAddress, solanaAddress } = useWallet()
	const t = useTranslations('depositMoonpay')

	const [selectedPlatform, setSelectedPlatform] = useState<DepositPlatform | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [iframeUrl, setIframeUrl] = useState<string | null>(null)

	// Polygon Safe address for Polymarket
	const polygonAddress = eoaAddress ? deriveSafeAddress(eoaAddress) : undefined

	// Check which platforms are available based on wallet + deposit config
	const isPolymarketEnabled = !!polygonAddress && !!MOONPAY_POLYGON_DEPOSIT_ID
	const isKalshiEnabled = !!solanaAddress && !!MOONPAY_SOLANA_DEPOSIT_ID

	const handleBack = () => {
		if (iframeUrl) {
			setIframeUrl(null)
			setSelectedPlatform(null)
		} else {
			closeModal()
			openModal('deposit-primary')
		}
	}

	const handleClose = () => {
		setSelectedPlatform(null)
		setError(null)
		setIframeUrl(null)
		closeModal()
	}

	const getRecipientAddress = () => {
		if (selectedPlatform === 'polymarket') return polygonAddress
		if (selectedPlatform === 'kalshi') return solanaAddress
		return undefined
	}

	const getDepositId = () => {
		if (selectedPlatform === 'polymarket') return MOONPAY_POLYGON_DEPOSIT_ID
		if (selectedPlatform === 'kalshi') return MOONPAY_SOLANA_DEPOSIT_ID
		return ''
	}

	const handleContinue = async () => {
		const recipientAddress = getRecipientAddress()
		const depositId = getDepositId()

		if (!recipientAddress) {
			setError(t('walletNotAvailable'))
			return
		}

		if (!depositId) {
			setError(t('notConfigured'))
			return
		}

		setIsLoading(true)
		setError(null)

		try {
			// Create deposit customer via our API route
			const response = await fetch('/api/moonpay/deposit-customer', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					customerId: recipientAddress,
					recipientWallet: recipientAddress,
					depositId, // Pass the platform-specific deposit ID
				}),
			})

			const data = await response.json()

			if (!response.ok) {
				throw new Error(data.error || 'Failed to create deposit session')
			}

			// Show MoonPay in iframe
			const moonpayUrl = `https://moonpay.hel.io/embed/deposit/${data.token}`
			setIframeUrl(moonpayUrl)
		} catch (err) {
			console.error('MoonPay deposit error:', err)
			const message = err instanceof Error ? err.message : 'Failed to initialize deposit'
			setError(message)
		} finally {
			setIsLoading(false)
		}
	}

	const hasWallet = polygonAddress || solanaAddress

	// Show iframe view
	if (iframeUrl) {
		return (
			<Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
				<DialogContent
					closeButtonClasses="size-5 text-white"
					closeButtonContainerClasses="top-2 right-3 cursor-pointer z-10"
					className="gap-0 overflow-hidden rounded-2xl border border-white/10 bg-brand-dark-light p-0 sm:max-w-[420px]"
				>
					<div className="flex items-center gap-2 border-white/10 border-b bg-brand-dark-light px-3 py-2">
						<Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleBack}>
							<ArrowLeft className="size-4 text-brand-gray-secondary" />
						</Button>
						{selectedPlatform && <PlatformBadge platform={selectedPlatform} className="size-5" />}
						<span className="font-medium text-[13px] text-white">
							{t('depositToIframe', {
								platform: t(`platforms.${selectedPlatform}`),
							})}
						</span>
					</div>
					{/* Overflow hidden container with negative margin to crop MoonPay's top padding */}
					<div className="relative h-[500px] overflow-hidden">
						<iframe
							src={iframeUrl}
							title="MoonPay Deposit"
							className="absolute inset-x-0 h-[calc(100%+16px)] w-full border-0"
							allow="accelerometer; autoplay; camera; gyroscope; payment; clipboard-read; clipboard-write; web-share"
							sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-modals"
						/>
					</div>
				</DialogContent>
			</Dialog>
		)
	}

	// Show platform selection view
	return (
		<Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
			<DialogContent
				closeButtonClasses="size-[24px] text-white"
				closeButtonContainerClasses="top-[15px] lg:top-[20px] lg:right-[26px] cursor-pointer"
				className="gap-[16px] rounded-3xl border border-white/10 bg-brand-dark-light p-[24px] sm:max-w-[420px]"
			>
				<DialogHeader className="relative gap-[8px]">
					<Button
						variant="ghost"
						size="icon"
						className="-ml-2 -top-[7px] absolute left-0 h-8 w-8"
						onClick={handleBack}
					>
						<ArrowLeft className="size-[24px] text-brand-gray-secondary" />
					</Button>
					<DialogTitle className="text-center font-inter font-semibold text-[18px] text-white leading-[100%]">
						{t('title')}
					</DialogTitle>
					<img src="/logos/moonpay.logo-full-white.svg" alt="MoonPay" className="mx-auto h-6" />
					<DialogDescription className="text-center text-[14px] text-brand-gray-secondary leading-[140%]">
						{t('description')}
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-[16px]">
					{hasWallet ? (
						<>
							{/* Platform selection */}
							<div className="flex flex-col gap-[8px]">
								<span className="text-[13px] text-brand-gray-secondary">{t('depositTo')}</span>
								<div className="flex flex-col gap-[8px]">
									{/* Polymarket option */}
									<button
										type="button"
										disabled={!isPolymarketEnabled}
										onClick={() => isPolymarketEnabled && setSelectedPlatform('polymarket')}
										className={cn(
											'flex items-center gap-3 rounded-[12px] border p-[16px] text-left transition-colors',
											selectedPlatform === 'polymarket'
												? 'border-brand-blue bg-brand-blue/10'
												: 'border-white/10 bg-white/5 hover:border-white/20',
											!isPolymarketEnabled && 'cursor-not-allowed opacity-50'
										)}
									>
										<PlatformBadge platform="polymarket" className="size-10" />
										<div className="flex flex-col gap-1">
											<span className="font-semibold text-[14px] text-white">
												{t('platforms.polymarket')}
											</span>
											<span className="text-[12px] text-brand-gray-secondary">
												{t('platforms.polymarketDescription')}
											</span>
										</div>
									</button>

									{/* Kalshi option */}
									<button
										type="button"
										disabled={!isKalshiEnabled}
										onClick={() => isKalshiEnabled && setSelectedPlatform('kalshi')}
										className={cn(
											'flex items-center gap-3 rounded-[12px] border p-[16px] text-left transition-colors',
											selectedPlatform === 'kalshi'
												? 'border-brand-blue bg-brand-blue/10'
												: 'border-white/10 bg-white/5 hover:border-white/20',
											!isKalshiEnabled && 'cursor-not-allowed opacity-50'
										)}
									>
										<PlatformBadge platform="kalshi" className="size-10" />
										<div className="flex flex-col gap-1">
											<span className="font-semibold text-[14px] text-white">
												{t('platforms.kalshi')}
											</span>
											<span className="text-[12px] text-brand-gray-secondary">
												{t('platforms.kalshiDescription')}
											</span>
										</div>
									</button>
								</div>
							</div>
							<span className="text-[14px] text-brand-muted">{t('feeDisclaimer')}</span>
							{/* Supported payment methods */}
							<div className="flex flex-col gap-[8px]">
								<span className="text-[13px] text-brand-gray-secondary">{t('paymentMethods')}</span>
								<div className="flex flex-wrap items-center gap-2">
									<span className="rounded-full bg-white/10 px-3 py-1.5 text-[12px] text-white">
										{t('payments.crypto')}
									</span>
									<span className="rounded-full bg-white/10 px-3 py-1.5 text-[12px] text-white">
										{t('payments.applePay')}
									</span>
									<span className="rounded-full bg-white/10 px-3 py-1.5 text-[12px] text-white">
										{t('payments.googlePay')}
									</span>
									<span className="rounded-full bg-white/10 px-3 py-1.5 text-[12px] text-white">
										{t('payments.card')}
									</span>
								</div>
							</div>

							{/* Error message */}
							{error && (
								<div className="rounded-[12px] border border-brand-red/20 bg-brand-red/10 p-[12px]">
									<p className="font-inter text-[13px] text-brand-red leading-[140%]">{error}</p>
								</div>
							)}

							{/* Continue button */}
							<Button
								onClick={handleContinue}
								disabled={!selectedPlatform || isLoading}
								className="h-[48px] w-full bg-brand-blue text-white hover:bg-brand-blue/90 disabled:opacity-50"
							>
								{isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
								{isLoading ? t('loading') : t('continue')}
							</Button>
						</>
					) : (
						<p className="text-center text-[14px] text-brand-gray-secondary">
							{t('connectWallet')}
						</p>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
